import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getPaymentByCampaignId } from '@/api/payments/get-payment'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { formatCurrency } from './utils'

export function FinanceTab({ campanhaId }: { campanhaId: number }) {
  const { data: payments, isLoading } = useQuery({
    queryKey: ['payments', campanhaId],
    queryFn: () => getPaymentByCampaignId(campanhaId),
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = payments?.slice(indexOfFirstItem, indexOfLastItem) || []
  const totalPages = Math.ceil((payments?.length || 0) / itemsPerPage)

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber)
  const nextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1)
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <div className="text-muted-foreground text-sm font-medium">
              Total Arrecadado
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(
                payments?.reduce(
                  (sum, payment) => sum + payment.valorUnitario,
                  0,
                ) || 0,
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <div className="text-muted-foreground text-sm font-medium">
              Títulos Vendidos
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{payments?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <div className="text-muted-foreground text-sm font-medium">
              Valor Médio
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(
                payments?.length
                  ? payments.reduce(
                      (sum, payment) => sum + payment.valorUnitario,
                      0,
                    ) / payments.length
                  : 0,
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Label htmlFor="itemsPerPage">Itens por página:</Label>
          <Select
            value={itemsPerPage.toString()}
            onValueChange={(value) => {
              setItemsPerPage(Number(value))
              setCurrentPage(1)
            }}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue placeholder={itemsPerPage} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-muted-foreground text-sm">
          {payments?.length || 0} itens no total
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <div className="min-w-[600px] sm:min-w-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentItems.length ? (
                currentItems.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.dataCadastro)}</TableCell>
                    <TableCell>{payment.titulo}</TableCell>
                    <TableCell>
                      {formatCurrency(payment.valorUnitario)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    Nenhum pagamento encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={prevPage}
          disabled={currentPage === 1}
        >
          Anterior
        </Button>

        <div className="flex items-center space-x-2">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNumber
            if (totalPages <= 5) {
              pageNumber = i + 1
            } else if (currentPage <= 3) {
              pageNumber = i + 1
            } else if (currentPage >= totalPages - 2) {
              pageNumber = totalPages - 4 + i
            } else {
              pageNumber = currentPage - 2 + i
            }

            return (
              <Button
                key={pageNumber}
                variant={currentPage === pageNumber ? 'default' : 'outline'}
                onClick={() => paginate(pageNumber)}
              >
                {pageNumber}
              </Button>
            )
          })}

          {totalPages > 5 && currentPage < totalPages - 2 && (
            <>
              <span className="px-2">...</span>
              <Button variant="outline" onClick={() => paginate(totalPages)}>
                {totalPages}
              </Button>
            </>
          )}
        </div>

        <Button
          variant="outline"
          onClick={nextPage}
          disabled={currentPage === totalPages}
        >
          Próxima
        </Button>
      </div>
    </div>
  )
}
